// Admin shell — Compliance/Manager layout. Lower-density, more
// whitespace, fewer keyboard shortcuts. Used by org admins for
// occasional governance tasks: trust bundles, retention rules,
// audit log review, maintenance toggle.
//
// Visually distinct from the daily-driver shell so admins know
// they're in "elevated" mode: nebula-violet accents instead of
// cyan-accent.
//
// v190 — switched from horizontal tab-row to grouped sidebar. With
// the surface count past 16 (after OIDC trust, Sigstore, Network,
// Banners landed) the horizontal row was overflowing on every
// laptop-sized viewport. Sidebar groups by purpose (governance /
// security / lifecycle / comms / system) so the cognitive load is
// 5 groups × ~3 items rather than 16 flat tabs.

import { useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import {
  Activity, ArrowLeft, BadgeCheck, Bell, Database, Download, ExternalLink, Gauge,
  Globe, Globe2, HelpCircle, LayoutDashboard, LifeBuoy,
  Lock, Megaphone, Menu, Palette, ScrollText,
  Shield, ShieldAlert, ShieldCheck, Timer, UserCog, Users, Wrench, X,
  type LucideIcon,
} from 'lucide-react';
import type { ReactNode } from 'react';
import { Wordmark } from './Logo';
import { PageContainer } from './PageContainer';
import { ThemeToggle } from './ui/ThemeToggle';
import { SearchBar } from './SearchBar';
import { UserMenu } from './UserMenu';
import { cn } from '../lib/cn';
import { useMaintenance } from '../api/maintenance';
import { usePublicConfig } from '../api/publicConfig';
import { MaintenanceBanner } from './MaintenanceBanner';
import { SystemBanner } from './SystemBanner';
import { SeedBanner } from './SeedBanner';
import { ImpersonationBanner } from './ImpersonationBanner';

interface AdminTab {
  to: string;
  label: string;
  Icon: LucideIcon;
}

interface AdminGroup {
  label: string;       // section heading (small caps)
  items: AdminTab[];
}

// Grouped surfaces. Order within each group is "most-used first".
//
// v191 sidebar cleanup — pulled four rarely-needed surfaces out of
// the main nav (Setup wizard, Demo data, API playground, Slug audit).
// All four remain reachable: Setup wizard + Demo data + API
// playground are surfaced as quick-action cards on the Overview page;
// Slug audit only appears as a banner on Overview when legacy slugs
// exist (item 62). Direct URLs still work — no route changes — so
// bookmarks and deep links don't break. Communications group merged
// into System: with only Notifications + Banners it was a sparse
// section header. Result: 23 → 19 surfaces, 6 → 5 groups.
const groups: AdminGroup[] = [
  {
    label: 'General',
    items: [
      { to: '/admin', label: 'Overview', Icon: LayoutDashboard },
    ],
  },
  {
    label: 'Identity & Access',
    items: [
      { to: '/admin/permissions', label: 'Permissions', Icon: Users },
      { to: '/admin/scim', label: 'SCIM', Icon: UserCog },
      { to: '/admin/quotas', label: 'Quotas', Icon: Gauge },
    ],
  },
  {
    label: 'Security',
    // v192 — three trust mechanics consolidated into one tabbed page
    // under `/admin/trust` (item 91 Phase A of PRODUCT-ROADMAP.md).
    // The X.509 / Sigstore / OIDC tabs all live there now; the
    // legacy `/admin/sigstore` + `/admin/oidc/policies` URLs redirect
    // onto the matching tab fragment in App.tsx so existing
    // bookmarks keep working.
    items: [
      { to: '/admin/trust', label: 'Trust', Icon: ShieldCheck },
      { to: '/admin/pull-gate', label: 'Pull gate', Icon: ShieldAlert },
      { to: '/admin/network', label: 'Network', Icon: Globe2 },
      { to: '/admin/audit', label: 'Audit log', Icon: ScrollText },
    ],
  },
  {
    label: 'Lifecycle',
    items: [
      { to: '/admin/imports', label: 'Imports', Icon: Download },
      { to: '/admin/retention', label: 'Retention', Icon: Timer },
      { to: '/admin/cnpg-catalog', label: 'CNPG catalog', Icon: Database },
      { to: '/admin/geo', label: 'Geo-sync', Icon: Globe },
    ],
  },
  {
    label: 'System',
    items: [
      { to: '/admin/notifications', label: 'Notifications', Icon: Bell },
      { to: '/admin/banners', label: 'Banners', Icon: Megaphone },
      { to: '/admin/branding', label: 'Branding', Icon: Palette },
      { to: '/admin/tool-switcher', label: 'Tool switcher', Icon: ExternalLink },
      { to: '/admin/license', label: 'License', Icon: BadgeCheck },
      // v192 — two compliance frameworks consolidated into one tabbed
      // page under `/admin/compliance` (item 91 Phase B of
      // PRODUCT-ROADMAP.md). The legacy `/admin/compliance/iso27001`
      // and `/admin/compliance/soc2` URLs redirect onto the matching
      // tab fragment in App.tsx so existing bookmarks keep working.
      { to: '/admin/compliance', label: 'Compliance', Icon: Shield },
      { to: '/admin/observability', label: 'Observability', Icon: Activity },
      { to: '/admin/maintenance', label: 'Maintenance', Icon: Wrench },
      // Item 100 Phase B — UI variant of `orbital diag`. Lives in
      // System rather than its own group; it's an occasional-use surface
      // tied to vendor-support escalations.
      { to: '/admin/support', label: 'Support', Icon: LifeBuoy },
    ],
  },
];

// Flat list still useful for the active-prefix detection (so e.g.
// /admin/imports doesn't light up when on /admin/imports/bulk).
const flatTabs: AdminTab[] = groups.flatMap((g) => g.items);

function SidebarNav({ onNavigate }: Readonly<{ onNavigate?: () => void }>) {
  return (
    <nav className="space-y-6 py-2">
      {groups.map((group) => (
        <div key={group.label} className="space-y-1">
          <p className="orb-section-title px-3 mb-1.5 text-fg-subtle">
            {group.label}
          </p>
          {group.items.map(({ to, label, Icon }) => {
            const hasChildTab = flatTabs.some(
              (o) => o.to !== to && o.to.startsWith(to + '/'),
            );
            return (
              <NavLink
                key={to}
                to={to}
                end={to === '/admin' || hasChildTab}
                onClick={onNavigate}
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-2.5 px-3 py-1.5 rounded-md text-sm transition-colors',
                    isActive
                      ? 'bg-nebula/10 text-nebula border border-nebula/30'
                      : 'text-fg-muted border border-transparent hover:text-fg hover:bg-bg-hover/40',
                  )
                }
              >
                <Icon className="h-4 w-4 shrink-0" />
                <span className="truncate">{label}</span>
              </NavLink>
            );
          })}
        </div>
      ))}
    </nav>
  );
}

export function AdminShell({ children, title, description }: Readonly<{
  children: ReactNode;
  // v184 — title accepts ReactNode so the Admin overview can embed
  // a version badge alongside the heading text.
  title?: ReactNode;
  description?: string;
}>) {
  const { data: maint } = useMaintenance();
  const { data: pubConfig } = usePublicConfig();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  return (
    <>
      <div className="starfield-bg" aria-hidden="true" />
      <div className="min-h-screen flex flex-col">
        <header className="relative z-40 h-16 border-b border-border bg-bg-elevated/60 backdrop-blur-sm flex items-center justify-between gap-6 px-6 shrink-0">
          <div className="flex items-center gap-4 shrink-0">
            {/* Mobile-nav toggle — only visible <lg */}
            <button
              type="button"
              onClick={() => setMobileNavOpen((v) => !v)}
              className="lg:hidden inline-flex items-center justify-center w-9 h-9 rounded-md text-fg-muted hover:text-fg hover:bg-bg-hover transition-colors"
              aria-label="Toggle admin navigation"
            >
              {mobileNavOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
            <Link to="/" className="flex items-center gap-2 text-fg-muted hover:text-fg text-sm">
              <ArrowLeft className="h-4 w-4" />
              <span className="hidden sm:inline">Back to app</span>
            </Link>
            <span className="text-fg-subtle hidden sm:inline">/</span>
            <span className="flex items-center gap-2">
              <Shield className="h-4 w-4 text-nebula" />
              <span className="orb-nav-label text-nebula">Admin</span>
            </span>
          </div>
          <div className="flex-1 max-w-3xl min-w-0 hidden md:block">
            <SearchBar />
          </div>
          <div className="flex items-center gap-3 shrink-0">
            {pubConfig?.air_gapped_mode && (
              <Link
                to="/admin/network"
                className="inline-flex items-center gap-1.5 px-2 h-7 rounded-md text-xs font-medium bg-danger/10 text-danger border border-danger/30 hover:bg-danger/20 transition-colors"
                title="This deployment is in air-gapped-by-default mode. Outbound network calls are denied unless an admin opted in."
              >
                <Lock className="h-3 w-3" />
                <span>Air-gapped</span>
              </Link>
            )}
            <Wordmark />
            {pubConfig?.help_url && (
              <a
                href={pubConfig.help_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center w-9 h-9 rounded-md text-fg-muted hover:text-fg hover:bg-bg-hover transition-colors"
                title="Help & documentation"
                aria-label="Help"
              >
                <HelpCircle className="h-4 w-4" />
              </a>
            )}
            <ThemeToggle />
            <UserMenu />
          </div>
        </header>

        <ImpersonationBanner />
        {maint?.active && <MaintenanceBanner reason={maint.reason} until={maint.until} />}
        {pubConfig?.active_banner && <SystemBanner banner={pubConfig.active_banner} />}
        {pubConfig?.seed_active && <SeedBanner setName={pubConfig.seed_set} />}

        <div className="flex-1 flex min-h-0">
          {/* Desktop sidebar — sticky, ~240px */}
          <aside className="hidden lg:block w-[240px] shrink-0 border-r border-border bg-bg-elevated/30 sticky top-16 self-start max-h-[calc(100vh-4rem)] overflow-y-auto">
            <div className="px-3 py-4">
              <SidebarNav />
            </div>
          </aside>

          {/* Mobile sidebar — drawer overlay */}
          {mobileNavOpen && (
            <div className="lg:hidden fixed inset-0 z-30">
              <button
                type="button"
                className="absolute inset-0 bg-black/50 backdrop-blur-sm"
                aria-label="Close navigation menu"
                onClick={() => setMobileNavOpen(false)}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') {
                    e.preventDefault();
                    setMobileNavOpen(false);
                  }
                }}
              />
              <div className="absolute top-16 left-0 bottom-0 w-72 bg-bg-elevated border-r border-border overflow-y-auto">
                <div className="px-3 py-4">
                  <SidebarNav onNavigate={() => setMobileNavOpen(false)} />
                </div>
              </div>
            </div>
          )}

          <main className="flex-1 min-w-0">
            <PageContainer className="py-10">
              {title && (
                <div className="mb-8">
                  <p className="orb-eyebrow text-nebula/80 mb-2">Governance</p>
                  <h1 className="text-3xl font-display font-semibold text-fg leading-tight">{title}</h1>
                  {description && (
                    <p className="text-fg-muted mt-3 max-w-2xl leading-relaxed">{description}</p>
                  )}
                </div>
              )}
              <div className="space-y-6">{children}</div>
            </PageContainer>
          </main>
        </div>
      </div>
    </>
  );
}
